// uninstall-global-tools-via-brew.ts
//
// Gỡ binary global mà avatar tự cài: gitnexus (npm) + gitleaks/trivy (brew).
// Gọi từ `avatar uninstall` (user chốt 2026-06-14: gỡ luôn, không hỏi).
//
// An toàn: chỉ gỡ binary nếu CÓ trong PATH (đang cài). gitnexus gỡ qua npm
// (avatar cài bằng npm), gitleaks/trivy gỡ qua brew (avatar cài bằng brew).
// Fail-soft: lỗi gỡ 1 tool → warn + tiếp tool khác, KHÔNG chặn uninstall.
//
// LƯU Ý: binary global dùng CHUNG mọi workspace. Gỡ ở đây = gỡ khỏi cả máy.
// User chốt chấp nhận (uninstall = dọn sạch). Workspace khác sẽ tự cài lại khi sync.

import { spawnSync } from "node:child_process";
import { log } from "./terminal-logger.js";

const PROBE_TIMEOUT_MS = 5000;
const UNINSTALL_TIMEOUT_MS = 2 * 60 * 1000;

interface GlobalTool {
  name: string;
  manager: "brew" | "npm";
}

// gitnexus: npm global. gitleaks/trivy: brew.
const GLOBAL_TOOLS: GlobalTool[] = [
  { name: "gitnexus", manager: "npm" },
  { name: "gitleaks", manager: "brew" },
  { name: "trivy", manager: "brew" },
];

function isInPath(name: string): boolean {
  const r = spawnSync("which", [name], { encoding: "utf8", timeout: PROBE_TIMEOUT_MS });
  return !r.error && r.status === 0 && (r.stdout || "").trim().length > 0;
}

function managerAvailable(manager: "brew" | "npm"): boolean {
  const r = spawnSync(manager, ["--version"], { encoding: "utf8", timeout: PROBE_TIMEOUT_MS });
  return !r.error && r.status === 0;
}

function uninstallOne(tool: GlobalTool): "removed" | "skipped" | "failed" {
  if (!isInPath(tool.name)) return "skipped"; // chưa cài → bỏ qua.
  if (!managerAvailable(tool.manager)) {
    log.warn(`  ! ${tool.name}: không có ${tool.manager} để gỡ — bỏ qua (gỡ tay nếu cần).`);
    return "skipped";
  }
  const args = tool.manager === "brew" ? ["uninstall", tool.name] : ["uninstall", "-g", tool.name];
  const r = spawnSync(tool.manager, args, {
    stdio: ["ignore", "pipe", "pipe"],
    timeout: UNINSTALL_TIMEOUT_MS,
    encoding: "utf8",
  });
  if (r.status !== 0) {
    log.warn(`  ! Gỡ ${tool.name} thất bại: ${(r.stderr || "").slice(0, 120)} — bỏ qua.`);
    return "failed";
  }
  return "removed";
}

// Gỡ tất cả global tool avatar đã cài. Fail-soft. In tóm tắt. KHÔNG throw.
export function uninstallGlobalToolsViaBrew(): void {
  log.info("Gỡ global tools avatar đã cài (gitnexus, gitleaks, trivy)...");
  const removed: string[] = [];
  const skipped: string[] = [];
  for (const tool of GLOBAL_TOOLS) {
    const result = uninstallOne(tool);
    if (result === "removed") removed.push(tool.name);
    else if (result === "skipped") skipped.push(tool.name);
  }
  if (removed.length) log.success(`  ✓ Đã gỡ: ${removed.join(", ")}`);
  if (skipped.length) log.dim(`  - Bỏ qua (chưa cài): ${skipped.join(", ")}`);
}
